import React, { useState } from 'react';

import { InfoOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Collapse,
  Grid,
  IconButton,
  Typography, useMediaQuery, useTheme,
} from '@mui/material';
import _ from 'lodash';
import {
  Bar,
  BarChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import {
  CircleDot, StarDot, TriangleDot,
} from '../../../components/ChartComponents';
import Tabs from '../../../components/Tabs';
import { SORT_OPTIONS } from '../constants';
import { AnalyticsData, SortableMarkType } from '../types';
import { getMistakeLabel } from '../utils';

import {
  Circle, InfoContainer, KeyContainer, LegendContainer, List,
  SortContainer, SortText, Title, TooltipContainer, TooltipLabel,
} from './index.styles';

type LegendProps = {
  payload?: any,
};

type TooltipProps = {
  payload?: any,
  label?: any,
};

// Legend formatter
export const PieChartLegend = ({ payload }: LegendProps) => (
  <List>
    {payload?.map((entry: any) => (
      <li key={entry.value}>
        <Typography variant="h5" color="textSecondary">
          {Math.round(entry.payload.value)}%
        </Typography>
        <KeyContainer>
          <Circle style={{ background: entry.color }} />
          <Typography color="textSecondary" fontSize={14}>
            {getMistakeLabel(entry.value)}
          </Typography>
        </KeyContainer>
      </li>
    ))}
  </List>
);

export const BarChartLegend = ({ payload }: LegendProps) => (
  <LegendContainer>
    {payload.filter((entry: any) => entry.value !== 'correctMarks')
      .map((entry: any) => (
        <KeyContainer key={entry.value}>
          <Circle style={{ background: entry.color }} />
          <Typography variant="subtitle1" color="textSecondary">
            {getMistakeLabel(entry.value)}
          </Typography>
        </KeyContainer>
      ))}
  </LegendContainer>
);

export const LineChartTooltip = ({ payload, label }: TooltipProps) => {
  const theme = useTheme();

  const renderDot = ({ name }: {name: string}) => {
    switch (name) {
      case 'average':
        return <TriangleDot cx={0} cy={0} fill={theme.palette.gray.main} />;
      case 'topMark':
        return <StarDot cx={0} cy={0} fill={theme.palette.mathsExt1.main} />;
      default:
        return <CircleDot cx={0} cy={0} fill={theme.palette.primary.main} />;
    }
  };
  // label is the x-axis label, which is the short label. Prefer to use the
  // full label from the payload if it exists (it should)
  // format the label for display
  const examLabel = payload?.[0]?.payload?.label || label;
  const isExamOrQuiz = /(Quiz|Exam)/.test(examLabel);
  const displayLabel = isExamOrQuiz ? examLabel : `${examLabel} Exam`;

  return (
    <TooltipContainer>
      {displayLabel}
      {(payload || []).map((entry: any) => (
        <KeyContainer key={entry.name}>
          {renderDot(entry)}
          <TooltipLabel>
            <Typography variant="subtitle1" color="textSecondary">
              {_.startCase(entry.name)}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {`${Math.round(entry.value)}%`}
            </Typography>
          </TooltipLabel>
        </KeyContainer>
      ))}
    </TooltipContainer>
  );
};

export const BarChartTooltip = ({ payload, label }: TooltipProps) => {
  // Sum up the incorrect marks
  const incorrectMarks = payload.reduce((acc: number, curr: any) => {
    if (curr.name !== 'correctMarks') {
      return acc + curr.value;
    }
    return acc;
  }, 0);

  return (
    <TooltipContainer>
      {label}
      {payload.filter((entry: any) => entry.name !== 'correctMarks')
        .map((entry: any) => (
          <KeyContainer key={entry.name}>
            <Circle style={{ background: entry.color }} />
            <TooltipLabel>
              <Typography variant="subtitle1" color="textSecondary">
                {getMistakeLabel(entry.name)}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {`${Math.round((entry.value * 100) / incorrectMarks) || 0}%`}
              </Typography>
            </TooltipLabel>
          </KeyContainer>
        ))}
    </TooltipContainer>
  );
};

interface CollapsableChartInfoProps {
  open: boolean,
  close: () => void,
  category: string
}

export const CollapsableChartInfo = ({ open = false, close, category }:
CollapsableChartInfoProps) =>
  (
    <Collapse in={open}>
      <Alert
        severity="info"
        // icon={false}
        action={(
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={close}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        )}
      >
        Each bar represents the likelihood of you losing marks for a
        particular {category.toLowerCase()}. This is calculated and scaled
        using our AI-powered algorithm to provide a more accurate reflection of your performance.
      </Alert>
    </Collapse>
  );

type AnalyticsBarChartProps = {
  sortBy: SortableMarkType,
  setSortBy: (sortBy: SortableMarkType) => void,
  data: AnalyticsData[] | undefined,
  category: string,
};

export const AnalyticsBarChart = ({ sortBy, setSortBy, data, category }:
AnalyticsBarChartProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const BARS = {
    incorrectMarks: <div key="incorrectMarks" />,
    correctMarks: <div key="correctMarks" />,
    inaccuracyMarks: <Bar
      key="inaccuracyMarks"
      dataKey="inaccuracyMarks"
      stackId="a"
      fill={theme.palette.mistake.dark}
    />,
    missedMarks: <Bar
      key="missedMarks"
      dataKey="missedMarks"
      stackId="a"
      fill={theme.palette.mistake.main}
    />,
    sillyMarks: <Bar
      key="sillyMarks"
      dataKey="sillyMarks"
      stackId="a"
      fill={theme.palette.mistake.light}
    />,
    correct: <Bar
      key="correct"
      dataKey="correctMarks"
      stackId="a"
      fill={theme.palette.backdrop.main}
      radius={[0, 5, 5, 0]}
    />,
  };

  return (
    <>
      <Grid
        container
        direction="row"
        alignItems="flex-start"
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={5}
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          <Title variant="h6">Error Likelihood by {category}</Title>
          <IconButton onClick={() => setIsInfoOpen((prev) => !prev)}>
            <InfoOutlined />
          </IconButton>
        </Grid>
        <Grid
          item
          xs={12}
          md={7}
          display="flex"
          justifyContent={isMd ? 'flex-end' : 'flex-start'}
        >
          <SortContainer $isMd={isMd}>
            <SortText>Sort by</SortText>
            <Tabs
              options={SORT_OPTIONS}
              selectedKey={sortBy}
              orientation="vertical"
              onChange={(key) => { setSortBy(key as SortableMarkType); }}
              desktopDropdown
            />
          </SortContainer>
        </Grid>
      </Grid>
      <InfoContainer>
        <CollapsableChartInfo
          open={isInfoOpen}
          close={() => setIsInfoOpen(false)}
          category={category}
        />
      </InfoContainer>
      <ResponsiveContainer width="100%" height={(data?.length ?? 1) * 40 + 60}>
        <BarChart data={data} layout="vertical">
          <Tooltip
            content={BarChartTooltip}
            wrapperStyle={{ zIndex: theme.zIndex.tooltip }}
            cursor={{
              fill: theme.palette.backdrop.dark,
            }}
          />
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{ fill: theme.palette.gray.dark }}
            tickFormatter={(value) => `${Math.round(value)}`}
            domain={[0, 100]}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={150}
            tick={{ fontSize: 14, fill: theme.palette.gray.dark }}
            axisLine={false}
            tickLine={false}
          />
          {BARS[sortBy]}
          {Object.keys(BARS).filter((key) => key !== sortBy)
            .map((key) => BARS[key as SortableMarkType])}
          {isSm && <Legend content={BarChartLegend} />}
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};
